<template>
    <div id="report29">
        <report-header
            :title="title"
            :isShowDateRange="true"
            :formatDateRangeTitle="dateRange"
        />

        <b-card>
            <div id="report-body" class="w-100">
                <div v-if="showDummyTable">
                    <p class="item-name">{{ dummyTableTitle }}</p>
                    <QTable
                        :columns="columns"
                        :data="dummyTableData"
                        hide-bottom
                        :loading="isLoading"
                        class="q-table"
                        :rows-per-page-options="[0]"
                    />
                </div>

                <div v-for="(records, i) in tableData" :key="i">
                    <p class="item-name">{{ records[0].name }}</p>
                    <QTable
                        :columns="columns"
                        :data="records"
                        hide-bottom
                        :loading="isLoading"
                        class="q-table"
                        :rows-per-page-options="[0]"
                    >
                        <template v-slot:body-cell="props">
                            <td v-if="props.col.name === 'date'">
                                {{ showDate(props.value) }}
                            </td>
                            <td
                                v-else-if="
                                    props.col.name === 'summary' ||
                                    props.col.name === 'detail' ||
                                    props.col.name === 'ordered_by' ||
                                    props.col.name === 'given_by'
                                "
                            >
                                <div v-html="props.value"></div>
                            </td>
                            <td v-else>
                                {{ props.value }}
                            </td>
                        </template>
                    </QTable>
                </div>
            </div>
        </b-card>
    </div>
</template>

<script>
import { QTable } from "quasar";
import "@/css/quasar-table.css";
import Report from "@/mixins/report";
const columnsDefinition = [
    {
        name: "date",
        field: "date",
        label: "Date",
        align: "left",
        headerStyle: "font-weight:bold; width:180px;"
    },
    {
        name: "summary",
        field: "summary",
        label: "Summary",
        align: "left",
        headerStyle: "font-weight:bold; width:300px;"
    },
    {
        name: "detail",
        field: "detail",
        label: "Detail",
        align: "left",
        headerStyle: "font-weight:bold; width:300px;"
    },
    {
        name: "ordered_by",
        field: "ordered_by",
        label: "Ordered By",
        align: "left",
        headerStyle: "font-weight:bold; width:180px;"
    },
    {
        name: "given_by",
        field: "given_by",
        label: "Administered By",
        align: "left",
        headerStyle: "font-weight:bold; width:180px;"
    },
    {
        name: "properQuantity",
        field: "properQuantity",
        label: "Quantity",
        align: "left",
        headerStyle: "font-weight:bold; width:100px;"
    },
    {
        name: "properBalance",
        field: "properBalance",
        label: "Balance",
        align: "left",
        headerStyle: "font-weight:bold; width:120px;"
    }
];

let reportElem;

export default {
    components: {
        QTable,
        ReportHeader: () => import("@/components/report/report-header")
    },
    mixins: [Report],
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: this.getStartDate("week"),
                    end: this.getEndDate("day"),
                };
            }
        }
    },
    data() {
        return {
            columns: columnsDefinition,
            tableData: [],
            isLoading: true,
            dateFormat: "",
            showDummyTable: true,
            dummyTableTitle: "Loading items..",
            dummyTableData: [],
            title: "Controlled Substances Log",
            dateRange: ""
        };
    },
    computed: {
        timestamp() {
            return moment
                .tz(this._timeZone)
                .format(this.getDateFormat() + " hh:mm A");
        },
        periodStart() {
            if (this.data.start) {
                return this.data.start.format(this.getDateFormat());
            }
            return null;
        },
        periodEnd() {
            if (this.data.end) {
                return this.data.end.format(this.getDateFormat());
            }
            return null;
        }
    },
    async mounted() {
        let self = this;
        // --- toggle navigation options
        self.data.isShowDateRange = true;
        self.data.isShowItemOptions = true;
        self.data.isShowProviderOptions = true;
        self.data.filterTitle = "Filter Report";
        self.data.reportId = 29; // tells filter dialog that 29 is using you
        self.data.isShowItemClass = true;

        await self._getSetting();
        self.data.start = self.getStartDate("week");
        self.data.end = self.getEndDate("day");
        self.data.from = self.converToUnix(self.data.start);
        self.data.to = self.converToUnix(self.data.end);
        self.dateFormat = self.getDateFormat();
        self.data.itemClass = 1;

        self.fetchData();

        self.$root.$on("report-filter-date-range:refresh", self.fetchData);

        self.$root.$on("controlled-substances-log:refresh", params => {
            self.refresh = 1;
            self.fetchData(params);
        });

        window.scrollTo(0, 0);
        // this.bindAnchorClick(); // enable this to use client side routing
    },
    beforeDestroy() {
        this.$root.$off("report-filter-date-range:refresh", this.fetchData);

        this.unbind();
        this.resetFilterModal();
    },
    methods: {
        resetFilterModal() {
            let self = this;
            self.data.reportId = null;
            self.data.isShowItemOptions = false;
            self.data.itemShowMode = 0;
            self.data.isShowProviderOptions = false;
            self.data.provider = null;
            self.data.items = null;
            self.data.itemClass = 1;
            self.data.isShowItemClass = false;
            self.data.filterTitle = "Filter Date";
        },
        bindAnchorClick() {
            reportElem = document.getElementById("report29");

            reportElem.addEventListener("click", event => {
                let target = event.target;
                if (target.matches("a")) {
                    let id = target.dataset.id;
                    let destination = target.dataset.to;

                    if (id && destination) {
                        event.preventDefault();
                        this.$router.push({
                            name: destination,
                            params: {
                                id: id
                            }
                        });
                    } else {
                        return true;
                    }
                }
            });
        },
        unbind() {
            if (reportElem) {
                reportElem.removeEventListener("click", null);
            }
        },
        showDate(val) {
            return moment.unix(val).tz(this._timeZone).format(this.dateFormat);
        },
        async fetchData(params = {}) {
            try {
                this.isLoading = true;
                this.dummyTableTitle = "Loading items...";
                this.dummyTableData = [];

                let uri = `${process.env.BARRAMUNDI}report/reports/29?`;
                let parameters = {};
                this.dynamicPageTitle(this.title);
                parameters.to = this.data.to;
                parameters.from = this.data.from;
                parameters.item_class = this.data.itemClass;
                parameters["filter[controlledItemData]"] = "";
                parameters["filter[controlledProviderData]"] = "";
                this.dateRange = this.getDateRange(this.data.from, this.data.to);

                // prettier-ignore
                if (params.providerShowMode === 2 && params.provider && params.provider.length > 0) {
                    parameters["filter[controlledProviderData]"] = params.provider.map(provider => provider.value);
                }

                // prettier-ignore
                if (params.itemShowMode === 2 && params.items && params.items.length > 0) {
                    parameters["filter[controlledItemData]"] = params.items.map(item => ':' + item.raw_id); // prettier-ignore
                }

                let res = await this.$http.get(uri, {
                    params: parameters
                });

                if (
                    res.data.response &&
                    res.data.response.message &&
                    res.data.response.message.code === 200
                ) {
                    this.isLoading = false;
                    let results = res.data.response.resource.results;
                    this.dateRange = this.getDateRange(
                        res.data.response.resource.meta.from,
                        res.data.response.resource.meta.to
                    );

                    if (Array.isArray(results) && results.length === 0) {
                        this.tableData = [];
                        this.showDummyTable = true;
                        this.dummyTableTitle = "No Items available";
                        this.dummyTableData = [{ date: "No record available" }];
                    } else {
                        this.showDummyTable = false;
                        this.tableData = this.properData(results);
                    }
                } else {
                    throw new Error("Error fetching data.");
                }
            } catch (e) {
                console.error(e);
                this.isLoading = false;
            }
        },
        properData(results) {
            const STARTING_BALANCE = "STARTING BALANCE";

            const negativeClosure = value => {
                let val = Number(value);

                if (val < 0) {
                    val = Math.abs(val);
                    val = val.toFixed(3);
                    return `(${val})`;
                }

                return val.toFixed(3);
            };

            for (let reportId in results) {
                let records = results[reportId];
                let runningBalance = 0;
                let unit;

                for (let n = 0; n < records.length; n++) {
                    let record = records[n];

                    if (
                        n === 0 &&
                        record.summary &&
                        record.summary.toUpperCase() === STARTING_BALANCE
                    ) {
                        runningBalance = Number(record.balance);
                        unit = record.value;
                    } else {
                        runningBalance = runningBalance + Number(record.amount);
                        record.balance = runningBalance;
                        unit = record.value;
                    }

                    // quantity format
                    if (record.amount !== "") {
                        let qty = negativeClosure(record.amount);
                        record.properQuantity = `${qty} ${unit}`;
                    } else {
                        record.properQuantity = "";
                    }

                    // balance format
                    let bal = negativeClosure(runningBalance);
                    record.properBalance = `${bal} ${unit}`;
                }

                // ending balance (only if record has Starting Balance)
                let firstRecord = records[0];
                if (
                    firstRecord &&
                    firstRecord.summary &&
                    firstRecord.summary.toUpperCase() === STARTING_BALANCE
                ) {
                    let endingBal = negativeClosure(runningBalance);
                    records.push({
                        date: this.data.end.unix(),
                        summary: "Ending balance",
                        detail: "",
                        ordered_by: "",
                        given_by: "",
                        properQuantity: "",
                        properBalance: `${endingBal} ${unit}`
                    });
                }
            }

            return results;
        }
    }
};
</script>

<style scoped>
.report-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 5px;
}
.report-heading .period {
    font-size: 0.7em;
}
th {
    font-weight: bold;
    white-space: nowrap;
}
.card-body {
    padding-top: 0;
    padding-bottom: 0;
}
.report-description {
    display: block;
    margin-bottom: 10px;
    font-style: italic;
}
.total {
    font-weight: bold;
    border-top: 3px double #e4e4e4;
}
p.item-name {
    font-weight: bold;
    font-size: 1.2em;
    margin: 20px 0 0 0;
    padding: 5px 15px;
    border-bottom: 1px solid #c6c6c6;
}
.q-table {
    margin-bottom: 40px;
}
</style>
